import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { faEdit, faTrash, faSearch, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import Pagination from '../../components/Pagination';
import ConfirmationModal from '../../components/ConfirmationModal';
import TextInput from '../../components/TextInput';
import Button from '../../components/Button';
import SkeletonLoader from '../../components/SkeletonLoader';
import leadStatusApi from '../../api/leadStatusApi';

const LeadStatus = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [itemsPerPage] = useState(10);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [statusToDeleteId, setStatusToDeleteId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [error, setError] = useState(null);

  const [lastPage, setLastPage] = useState(1);

  const queryParams = new URLSearchParams(location.search);
  const currentPage = parseInt(queryParams.get('page')) || 1;

  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchTerm]);

  const fetchStatuses = useCallback(async (page, perPage, search) => {
    setLoading(true);
    setError(null);
    try {
      // Assuming backend API returns data ordered by updated_at descending by default
      const response = await leadStatusApi.getLeadStatuses({ page, perPage, search, includeTrashed: false });
      setStatuses(response.data);
      setLastPage(response.pagination.last_page);
    } catch (err) {
      console.error('Failed to fetch lead statuses:', err);
      setError('Failed to load lead statuses. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatuses(currentPage, itemsPerPage, debouncedSearchTerm);
  }, [fetchStatuses, currentPage, itemsPerPage, debouncedSearchTerm]);

  const handleAddStatus = () => {
    navigate('/lead-status/create');
  };

  const handleGoToTrash = () => {
    navigate('/lead-status/trash');
  };

  const handleEditStatus = (id) => {
    navigate(`/lead-status/${id}/edit`);
  };

  const handleDeleteClick = (id) => {
    setStatusToDeleteId(id);
    setIsModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    setIsModalOpen(false);
    if (statusToDeleteId) {
      setLoading(true);
      setError(null);
      try {
        await leadStatusApi.trashLeadStatus(statusToDeleteId);
        await fetchStatuses(currentPage, itemsPerPage, debouncedSearchTerm);
      } catch (err) {
        console.error('Failed to trash lead status:', err);
        setError('Failed to move lead status to trash. Please try again.');
      } finally {
        setLoading(false);
        setStatusToDeleteId(null);
      }
    }
  };

  const handleCancelDelete = () => {
    setIsModalOpen(false);
    setStatusToDeleteId(null);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('page', 1);
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  return (
    <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white flex flex-col min-h-[calc(100vh-100px)]">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6"> {/* Modified for responsiveness */}
        <h1 className="text-3xl font-bold text-text-white mb-4 sm:mb-0">Lead Status Management</h1> {/* Added mb-4 for mobile spacing */}
        <div className="flex space-x-4">
          <Button
            onClick={handleAddStatus}
            className="bg-accent-red hover:bg-red-700 text-white"
          >
            Add New Status
          </Button>
          <Button
            onClick={handleGoToTrash}
            className="bg-gray-600 hover:bg-gray-700 text-white"
            icon={faTrashAlt}
            tooltipId="trash-lead-status-tooltip"
            tooltipContent="View Trashed Lead Statuses"
          >
            Trash
          </Button>
        </div>
      </div>

      <div className="mb-4">
        <TextInput
          label="Search Lead Statuses"
          id="search-lead-statuses"
          name="search-lead-statuses"
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Search by status name..."
          icon={faSearch}
        />
      </div>

      {loading ? (
        <SkeletonLoader type="table" count={itemsPerPage} />
      ) : error ? (
        <div className="text-red-500 text-center py-4">{error}</div>
      ) : (
        <>
          <div className="overflow-x-auto flex-grow">
            <table className="min-w-full bg-gray-800 border border-gray-700">
              <thead>
                <tr>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">ID</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Status</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Color</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Is Default</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Updated At</th> {/* New column */}
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Actions</th>
                </tr>
              </thead>
              <tbody>
                {statuses.length > 0 ? (
                  statuses.map((status) => (
                    <tr key={status.id} className="hover:bg-gray-700">
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">{status.id}</td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">{status.name}</td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">
                        <div
                          className="w-6 h-6 rounded-full border border-gray-600"
                          style={{ backgroundColor: status.color }}
                        ></div>
                      </td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">
                        {status.is_default ? 'Yes' : 'No'}
                      </td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">
                        {new Date(status.updated_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </td> {/* Display updated_at */}
                      <td className="py-3 px-4 border-b border-gray-700 text-sm flex items-center flex-nowrap">
                        <Button
                          onClick={() => handleEditStatus(status.id)}
                          className="text-yellow-500 hover:text-yellow-600 mr-3"
                          icon={faEdit}
                          tooltipId={`edit-lead-status-tooltip-${status.id}`}
                          tooltipContent="Edit"
                        />
                        <Button
                          onClick={() => handleDeleteClick(status.id)}
                          className="text-red-600 hover:text-red-700"
                          icon={faTrash}
                          tooltipId={`delete-lead-status-tooltip-${status.id}`}
                          tooltipContent="Delete"
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="py-3 px-4 text-center text-sm text-text-gray">No lead statuses found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={lastPage}
          />
        </>
      )}

      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Confirm Delete"
        message="Are you sure you want to move this lead status to trash?"
      />
    </div>
  );
};

export default LeadStatus;